import React, { useState, useEffect, useRef } from "react";
import { CGM_WARMUP_DURATION_SECONDS } from "src/constants/cgm";
import MeshGradient from "src/assets/images/cgm/connection-journey/mesh-gradient.png";

interface WarmupTimerProps {
  /** ISO date string: when warmup ends (initEndDate from PocDevice) */
  warmupEndDate: string;
  /** Called when warmup countdown reaches zero */
  onComplete?: () => void;
}

const WarmupTimer: React.FC<WarmupTimerProps> = ({
  warmupEndDate,
  onComplete,
}) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const endTime = new Date(warmupEndDate).getTime();
    if (Number.isNaN(endTime)) return CGM_WARMUP_DURATION_SECONDS;
    const diffMs = endTime - Date.now();
    return Math.max(0, Math.floor(diffMs / 1000));
  });

  useEffect(() => {
    const endTime = new Date(warmupEndDate).getTime();
    console.log(
      "[CGM] WarmupTimer — warmupEndDate:",
      warmupEndDate,
      "| endTime:",
      endTime,
      "| now:",
      Date.now(),
      "| remainMs:",
      endTime - Date.now()
    );

    const calculateRemaining = () => {
      const now = Date.now();
      const diffMs = endTime - now;
      return Math.max(0, Math.floor(diffMs / 1000));
    };

    const initial = calculateRemaining();
    setRemainingSeconds(initial);

    if (initial <= 0) {
      queueMicrotask(() => onCompleteRef.current?.());
      return;
    }

    const interval = setInterval(() => {
      const remaining = calculateRemaining();
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onCompleteRef.current?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [warmupEndDate]);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  const progress =
    ((CGM_WARMUP_DURATION_SECONDS - remainingSeconds) /
      CGM_WARMUP_DURATION_SECONDS) *
    100;
  const radius = 120;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  // NOTE: the progress SVG is rotated -90deg, so angle starts at 3 o'clock here.
  const angle = (progress / 100) * 2 * Math.PI;
  const dotX = 130 + radius * Math.cos(angle);
  const dotY = 130 + radius * Math.sin(angle);

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-64 h-64 rounded-full overflow-hidden">
        {/* Mesh background clipped to circle */}
        <div className="absolute inset-0">
          <img
            src={MeshGradient}
            alt="Mesh Gradient"
            className="w-full h-full object-cover opacity-100 blur-[2px] pointer-events-none select-none"
          />
        </div>

        {/* Dotted ring (sits above mesh, below progress) */}
        <svg
          className="absolute top-0 left-0 w-full h-full"
          viewBox="0 0 260 260"
        >
          <circle
            cx="130"
            cy="130"
            r={radius}
            fill="none"
            stroke="#1a6e3d"
            strokeOpacity="0.55"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="0.1 10"
          />
        </svg>

        <svg
          className="absolute top-0 left-0 w-full h-full -rotate-90"
          viewBox="0 0 260 260"
        >
          <circle
            cx="130"
            cy="130"
            r={radius}
            fill="none"
            stroke="url(#warmupGradient)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500 ease-in-out"
            // className="transition-all duration-500 ease-in-out drop-shadow-[0_6px_14px_rgba(26,110,61,0.28)]"
          />
          {/* Progress end dot */}
          <circle
            cx={dotX}
            cy={dotY}
            r="7"
            fill="#1a6e3d"
            // className="drop-shadow-[0_6px_14px_rgba(26,110,61,0.35)]"
          />
          <defs>
            <linearGradient
              id="warmupGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#8de4af" />
              <stop offset="100%" stopColor="#1a6e3d" />
            </linearGradient>
          </defs>
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="relative z-10 flex flex-col items-center">
            <span className="text-5xl font-bold text-gray-900">
              {formattedMinutes}:{formattedSeconds}
            </span>
            <span className="text-sm text-gray-600 mt-1">remaining</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarmupTimer;
